import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { CompanyLocation, updateTabUser } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface EditTableUsernameModalProps extends ModalProps {
  location: CompanyLocation;
}

export function EditTableUsernameModal({
  location,
  ...props
}: EditTableUsernameModalProps) {
  const { t } = useTranslation();
  const tabUserSchema = z.object({
    userName: z.string().min(1, t("common.Username is required")),
    userId: z.string().min(1, t("common.User Id is required")),
  });

  type TabUserData = z.infer<typeof tabUserSchema>;
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<TabUserData>({
    resolver: zodResolver(tabUserSchema),
    defaultValues: {
      userName: location?.tabUser?.userName || "",
      userId: location?.tabUser?.userId || "",
    },
  });

  const mutation = useMutation({
    mutationFn: updateTabUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["licensesAndTabUsers"] });
      props?.onClose?.();
      toast.success(t("location.The tablet username has been changed"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("location.Unable to change the tablet username"), {
        duration: 4000,
      });
    },
  });

  const onSubmit = (data: TabUserData) => {
    mutation.mutate(data);
  };

  useEffect(() => {
    reset({
      userName: location?.tabUser?.userName || "",
      userId: location?.tabUser?.userId || "",
    });
  }, [location, reset]);

  return (
    <Modal
      title={t("location.Edit Tablet Username", {
        locationName: location?.name,
      })}
      actionButtons={
        <>
          <Button
            variant="secondary"
            type="button"
            onClick={() => props?.onClose?.()}
          >
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            loading={mutation.isPending}
          >
            {t("common.Save Changes")}
          </Button>
        </>
      }
      {...props}
    >
      <form className="flex flex-col gap-5">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row justify-between items-center">
              <label className="form-label flex gap-1 max-w-56 font-normal">
                {t("common.Username")}
                <span className="text-red-500">*</span>
              </label>
              <Input
                size="sm"
                {...register("userName")}
                defaultValue={location?.tabUser?.userName}
              />
            </div>
            <div className="flex justify-end">
              <ErrorMessage message={errors?.userName?.message} />
            </div>
          </div>

          <input
            {...register("userId")}
            type="hidden"
            defaultValue={location?.tabUser?.userId}
          />
          <ErrorMessage message={errors?.userId?.message} />
        </div>
      </form>
    </Modal>
  );
}
